import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from '@consta/uikit/Table';
import cn from 'classnames/bind';
import styles from './styles.styl';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { Theme } from '@consta/uikit/Theme';
import { myPreset } from 'src/utils/myPreset';
import DirectoryInfo from 'src/components/DirectoryInfo';
import {
  ColumnsJournalTable,
  NumberLinesItems,
  RowsJournalTable,
} from 'src/utils/config';
import NumberLines, { ItemType } from 'src/components/NumberLines/NumberLines';
import { AccessorForTablesEnum, FilterPageEnum } from 'src/utils/enum';
import JournalFilter from './JournalFilter';
import { IJournalCurrentFilters } from './JournalFilter/JournalFilter';
import { useSearchParams } from 'react-router-dom';
import { getJournal } from 'src/redux/features/journal/actions';
import { useDispatch } from 'react-redux';
import { journalSelector } from 'src/redux/features/journal/selectors';
import { useShallowEqualSelector } from 'src/customHooks/useShallowEqualSelector';
import { State } from 'src/redux/reducer';
import { fulfilled, pending } from 'src/redux/constants';
import { changeDateFormatDDMMYYYYTIME } from 'src/utils';
import { useDebouncedCallback } from 'use-debounce';
import {
  userInitLoadedSelector,
  userInitSelector,
} from 'src/redux/features/userInit/selectors';
import { IJournalItem } from 'src/redux/features/journal/types';
import SidebarJournal from './SidebarJournal';
import TableLoader from 'src/components/TableLoader';
import produce from 'immer';
import CustomTextTooltip from 'src/components/CustomTextTooltip';
import auditApi from 'src/utils/api/auditApi';
import modelsApi from 'src/utils/api/modelsApi';

const cx = cn.bind(styles);

export interface UploadFilesData {
  id: number;
  path: string;
  filename: string;
  file_type: number;
  approve: boolean;
}

export interface IBaseFilter {
  page: string;
  per_page: string;
  username?: string;
  mod?: string;
}

const Journal: React.FC = () => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [sidebarItemDetails, setSidebarItemDetails] = useState(null);
  const [selectRow, setSelectRow] = useState(undefined);
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);

  const [activeNumber, setActiveNumber] = useState(NumberLinesItems[0]);

  useEffect(() => setRows(RowsJournalTable), []);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const [searchValue, setSearchValue] = useState(
    params?.username ? params.username : '',
  );

  const [filter, setFilter] = useState<Partial<IJournalCurrentFilters>>({});

  const [{ pages, items, status, count }, isUserInitLoaded, userInit] =
    useShallowEqualSelector((state: State) => [
      journalSelector(state),
      userInitLoadedSelector(state),
      userInitSelector(state),
    ]);

  const clearFilter = () => {
    applyFilters({});
    setSearchValue(null);
  };

  const [currentPage, setCurrentPage] = useState(
    params?.page ? Number(params?.page) : 1,
  );

  const filtersAreNotEmpty: boolean = useMemo(
    () =>
      Object.keys(filter).some(
        (key: keyof IJournalCurrentFilters) => !!filter[key],
      ) || !!params?.fullname,
    [filter, params],
  );

  const handleAllSearch = useDebouncedCallback((value: string) => {
    searchParams.set(FilterPageEnum.PAGE, '1');
    if (!value) {
      searchParams.delete(FilterPageEnum.USERNAME);
      setSearchParams(searchParams);
    } else if (value?.length > 2) {
      searchParams.set(FilterPageEnum.USERNAME, value);
      setSearchParams(searchParams);
    }
  }, 800);

  const handleSearchName = useCallback(
    (value: string) => {
      setSearchValue(value);
      handleAllSearch(value);
    },
    [handleAllSearch],
  );

  const handleClickFilterIcon = () => {
    setIsAdvancedFilterOpen((prev) => !prev);
  };

  const closeFilter = useCallback(
    () => setIsAdvancedFilterOpen(false),
    [setIsAdvancedFilterOpen],
  );

  useEffect(() => {
    location.search && dispatch(getJournal(location.search));
  }, [dispatch, location.search]);

  useEffect(() => {
    if (Object.keys(params).length) {
      setCurrentPage(params?.page ? Number(params?.page) : 1);
      setActiveNumber(
        params?.per_page
          ? { id: Number(params?.per_page), label: Number(params?.per_page) }
          : NumberLinesItems[0],
      );
      setSearchValue(params?.fullname ? params.fullname : '');
    } else {
      setSearchParams(
        {
          [FilterPageEnum.PER_PAGE]: String(activeNumber.label),
          [FilterPageEnum.PAGE]: String(currentPage),
        },
        {
          replace: true,
        },
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const data: { [key: string]: string | number | string[] } = {};

    searchParams.forEach(
      (value, key: keyof Partial<IJournalCurrentFilters> | FilterPageEnum) => {
        if (
          key !== FilterPageEnum.PAGE &&
          key !== FilterPageEnum.PER_PAGE &&
          key !== 'login' &&
          key !== 'methods'
        ) {
          data[key as keyof IJournalCurrentFilters] = value;
        }
        if (key === 'methods') {
          if (!data.methods) {
            data.methods = [];
          }
          if (Array.isArray(data.methods)) {
            data.methods.push(value);
          }
        }
      },
    );

    setFilter((prev) => ({ ...prev, ...data }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInit, isUserInitLoaded]);

  const applyFilters = useCallback((filter) => {
    setFilter(filter);
    const data = Object.fromEntries(
      Object.keys(filter)
        .filter(
          (key: keyof Partial<IJournalCurrentFilters>) => filter[key] !== null,
        )
        .map((key: keyof Partial<IJournalCurrentFilters>) => [
          key,
          filter[key],
        ]),
    );
    setCurrentPage(1);
    const baseFilter: IBaseFilter = {
      page: '1',
      per_page: String(activeNumber.id),
    };
    if (searchValue) {
      baseFilter.username = searchValue;
    }
    setSearchParams(
      { ...baseFilter, ...data },
      {
        replace: true,
      },
    );
  }, []);

  const changePaginationPerPage = useCallback(
    (value: ItemType) => {
      searchParams.set(FilterPageEnum.PER_PAGE, String(value.label));
      setSearchParams(searchParams);
      setActiveNumber(value);
    },
    [searchParams, setSearchParams],
  );

  const onChangePagination = useCallback(
    (value: number) => {
      searchParams.set(FilterPageEnum.PAGE, String(value + 1));
      setSearchParams(searchParams);
      setCurrentPage(value + 1);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (status === fulfilled) {
      const convertedRows = produce(items, (draft: IJournalItem[]) => {
        draft.forEach((item) => {
          item.created_at = changeDateFormatDDMMYYYYTIME(
            new Date(item.created_at),
          );
          if (item.user) {
            Object.keys(item.user).forEach((key) => {
              if (!item.user[key]) {
                item.user[key] = '—';
              }
            });
          } else {
            item.user = {
              id: 0,
              fullname: '—',
              role: { id: 0, name: '—' },
              login: '—',
            };
          }
        });

        return draft;
      });
      setRows(convertedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, status]);

  const columns = ColumnsJournalTable.map(
    ({ title, accessor, ...rest }): any => {
      if (accessor === AccessorForTablesEnum.Login) {
        return {
          title: (
            <Text
              size="s"
              weight="regular"
              className={cx('Journal__tableHeader')}
            >
              {title}
            </Text>
          ),
          accessor,
          renderCell: (row: any) => (
            <CustomTextTooltip
              className={cx('Journal__ellipsis')}
              text={row.user ? row.user.username : ''}
              size="s"
            />
          ),
          ...rest,
        };
      }
      if (accessor === AccessorForTablesEnum.Role) {
        return {
          title: (
            <Text
              size="s"
              weight="regular"
              className={cx('Journal__tableHeader')}
            >
              {title}
            </Text>
          ),
          accessor,
          renderCell: (row: any) => (
            <Text size="s" className={cx('Journal__ellipsis')}>
              {row.user && userInit?.roles
                ? userInit.roles.find((role) => role.key === row.user.role_name)
                    ?.name
                : ''}
            </Text>
          ),
          ...rest,
        };
      }
      if (accessor === AccessorForTablesEnum.Event) {
        return {
          title: (
            <Text
              size="s"
              weight="regular"
              className={cx('Journal__tableHeader')}
            >
              {title}
            </Text>
          ),
          accessor,
          renderCell: (row: any) => {
            return (
              <CustomTextTooltip
                className={cx('Journal__ellipsis')}
                text={row?.event || '-'}
                size="s"
              />
            );
          },
          ...rest,
        };
      }
      if (accessor === AccessorForTablesEnum.Service) {
        return {
          title: (
            <Text
              size="s"
              weight="regular"
              className={cx('Journal__tableHeader')}
            >
              {title}
            </Text>
          ),
          accessor,
          renderCell: (row: any) => {
            return (
              <CustomTextTooltip
                className={cx('Journal__ellipsis')}
                text={row?.service || '-'}
                size="s"
              />
            );
          },
          ...rest,
        };
      }
      return {
        title: (
          <Text
            size="s"
            weight="regular"
            className={cx('Journal__tableHeader')}
          >
            {title}
          </Text>
        ),
        accessor,
        ...rest,
      };
    },
  );

  const getModelName = async (id: string) => {
    const model = await modelsApi.loadOneModels(id);
    return model.name;
  };

  const handleClickRow = async ({
    id,
  }: {
    id: string | undefined;
  }): Promise<void> => {
    if (id) {
      setSelectRow(id);
      const data = await auditApi.getJournalMethods(
        id,
        '?page=1&per_page=1000',
      );

      if (data.items[0]?.data_changed?.model_id) {
        data.items[0].data_changed.model_name = await getModelName(
          data.items[0].data_changed.model_id,
        );
      }
      setSidebarItemDetails({
        methodDetails: data,
        commonInfo: rows.find((item) => item.id === id),
      });
      setSidebarOpen(true);
    }
  };

  return (
    <div className={cx('Journal')}>
      <DirectoryInfo
        viewButton="secondary"
        handleSearch={handleSearchName}
        searchValue={searchValue}
        handleClickFilter={handleClickFilterIcon}
        className={cx('Journal__directoryInfo')}
        hasFilter={Object.keys(filter).length > 0}
        plcSeach={'Поиск по логину'}
      />
      {filtersAreNotEmpty && (
        <div className={cx('Journal__tableInfoFilter')}>
          <Text size="xs">Найдено: {count}</Text>
          <Button
            size="xs"
            view="ghost"
            label="Сбросить все фильтры"
            onClick={clearFilter}
          />
        </div>
      )}
      {status === pending ? (
        <TableLoader />
      ) : (
        <Theme preset={myPreset}>
          <Table
            columns={columns}
            rows={rows}
            zebraStriped="even"
            size="m"
            activeRow={{ id: selectRow, onChange: handleClickRow }}
            verticalAlign="center"
            className={cx('Journal__table', {
              Journal__table_filter: filtersAreNotEmpty,
            })}
            stickyHeader
          />
        </Theme>
      )}
      <SidebarJournal
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        selectRow={selectRow}
        setSelectRow={setSelectRow}
        sidebarItemDetails={sidebarItemDetails}
      />
      <JournalFilter
        isOpen={isAdvancedFilterOpen}
        close={closeFilter}
        filter={filter}
        setFilter={applyFilters}
        clearFilter={clearFilter}
      />
      <div className={cx('Journal__pag')}>
        {pages && (
          <NumberLines
            valuePage={activeNumber}
            handleChange={changePaginationPerPage}
            currentPage={currentPage - 1}
            totalPages={pages}
            onChangePagination={onChangePagination}
          />
        )}
      </div>
    </div>
  );
};
export default Journal;
